import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import type { DailySales, HourlySales, ChannelBreakdown, TopProduct, PeakDay, PeakHour } from '../../lib/metrics-hooks';
// ─── Custom Tooltip ────────────────────────────────────────
function ChartTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm" data-id="element-877" data-mp="1kfcdhac4jjk1">
      <p className="font-medium text-gray-300 mb-1" data-id="element-878" data-mp="1fghsc1qopiwr">{label}</p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-879" data-mp="3er93cyux4hcz" data-mpi={i}>
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color
      }} data-id="element-880" data-mp="39tqwde0goc7m" />
          <span className="text-gray-400" data-id="element-881" data-mp="twtxlja9q7a">{entry.name}:</span>
          <span className="font-semibold" data-id="element-882" data-mp="5ukuaxtejje6">
            {isCurrency ? formatCurrency(entry.value) : entry.value}
          </span>
        </p>)}
    </div>;
}
// ─── Daily Sales Chart ─────────────────────────────────────
interface DailySalesChartProps {
  data: DailySales[];
  peakDays: PeakDay[];
}
export function DailySalesChart({
  data,
  peakDays
}: DailySalesChartProps) {
  const [metric, setMetric] = useState<'revenue' | 'orders'>('revenue');
  const peakDates = new Set(peakDays.map(p => p.date));
  const chartData = data.map(d => ({
    ...d,
    isPeak: peakDates.has(d.date),
    displayLabel: `${d.dayOfWeek} ${d.label}`
  }));
  return <Card data-id="element-883" data-mp="fq5qgfbshpg0">
      <CardHeader data-id="element-884" data-mp="13i350e6mivkt">
        <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-885" data-mp="13hjj6mdurn1i">
          <CardTitle data-id="element-886" data-mp="188t97o3bcsdx">Ventas por Día</CardTitle>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5" data-id="element-887" data-mp="189l5700tos4q">
            <button onClick={() => setMetric('revenue')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${metric === 'revenue' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-888" data-mp="1i5bkhr6ytn2l">
              Ingresos
            </button>
            <button onClick={() => setMetric('orders')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${metric === 'orders' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-889" data-mp="138ao3adtx3xs">
              Órdenes
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent data-id="element-890" data-mp="pdrv1pjrzcs5">
        {data.length === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-891" data-mp="ki2qt8wt97pi">
            No hay datos para el período seleccionado
          </div> : <div className="h-72" data-id="element-892" data-mp="bglb3rnbf81c">
            <ResponsiveContainer width="100%" height="100%" data-id="element-893" data-mp="bhmow3vpu8mr">
              <AreaChart data={chartData} margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0
          }} data-id="element-894" data-mp="2n3ym2xoaom20">
                <defs data-id="element-895" data-mp="2w5eks85qyzqe">
                  <linearGradient id="dailyGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-896" data-mp="2w5692jld20gs">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} data-id="element-897" data-mp="23hit5hv1by0h" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-898" data-mp="28fke68c0u7el" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} data-id="element-899" data-mp="2dc2dgsol1djj" />
                <XAxis dataKey="label" tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={{
              stroke: '#e5e7eb'
            }} interval={data.length > 15 ? Math.floor(data.length / 10) : 0} data-id="element-900" data-mp="1koex5puch92p" />
                <YAxis tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={false} tickFormatter={v => metric === 'revenue' ? `₡${(v / 1000).toFixed(0)}k` : String(v)} data-id="element-901" data-mp="4ynryvbg8g6c" />
                <Tooltip content={<ChartTooltip isCurrency={metric === 'revenue'} data-id="element-903" data-mp="ixnf1yjbcn0e" />} data-id="element-902" data-mp="nw88nf4288fv" />
                <Area type="monotone" dataKey={metric} name={metric === 'revenue' ? 'Ingresos' : 'Órdenes'} stroke="#10b981" strokeWidth={2} fill="url(#dailyGradient)" dot={(props: any) => {
              const {
                cx,
                cy,
                payload
              } = props;
              if (payload.isPeak) {
                return <circle key={`peak-${payload.date}`} cx={cx} cy={cy} r={5} fill="#ef4444" stroke="#fff" strokeWidth={2} data-id="element-905" data-mp="rdmgq3umxtr5" />;
              }
              return <circle key={`dot-${payload.date}`} cx={cx} cy={cy} r={0} data-id="element-906" data-mp="8h4qjqvfq1x0" />;
            }} data-id="element-904" data-mp="ivu7mh0vlyrc" />
              </AreaChart>
            </ResponsiveContainer>
          </div>}
        {peakDays.length > 0 && <div className="mt-3 flex items-center gap-4 text-xs text-gray-500" data-id="element-907" data-mp="3fsw7tyeb2n7f">
            <span className="flex items-center gap-1.5" data-id="element-908" data-mp="3at9uzq2e38z5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500" data-id="element-909" data-mp="3arqdjw4zyrvs" />
              Días pico (mayor volumen)
            </span>
          </div>}
      </CardContent>
    </Card>;
}
// ─── Hourly Sales Chart ────────────────────────────────────
interface HourlySalesChartProps {
  data: HourlySales[];
  peakHours: PeakHour[];
}
export function HourlySalesChart({
  data,
  peakHours
}: HourlySalesChartProps) {
  const peakHourSet = new Set(peakHours.map(p => p.hour));
  // Only show hours with activity + 1 hour padding
  const activeHours = data.filter(h => h.orders > 0);
  const minHour = activeHours.length > 0 ? Math.max(0, activeHours[0].hour - 1) : 6;
  const maxHour = activeHours.length > 0 ? Math.min(23, activeHours[activeHours.length - 1].hour + 1) : 22;
  const filteredData = data.filter(h => h.hour >= minHour && h.hour <= maxHour);
  return <Card data-id="element-910" data-mp="ongc62iy6d5i">
      <CardHeader data-id="element-911" data-mp="onq43vnjyudu">
        <CardTitle data-id="element-912" data-mp="oo8ahl3xiozh">Distribución por Hora</CardTitle>
      </CardHeader>
      <CardContent data-id="element-913" data-mp="tli4zf66dqu6">
        {activeHours.length === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-914" data-mp="yjjou2bfdu6p">
            No hay datos para el período seleccionado
          </div> : <div className="h-72" data-id="element-915" data-mp="17krankwr92jw">
            <ResponsiveContainer width="100%" height="100%" data-id="element-916" data-mp="17lj5gtij2w94">
              <BarChart data={filteredData} margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0
          }} data-id="element-917" data-mp="17iwxisrq6eqd">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} data-id="element-918" data-mp="1m9x7x3c18ip5" />
                <XAxis dataKey="label" tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={{
              stroke: '#e5e7eb'
            }} data-id="element-919" data-mp="1q9edrxvdfhuu" />
                <YAxis tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={false} data-id="element-920" data-mp="2qj6nkrsfbl70" />
                <Tooltip content={<ChartTooltip isCurrency={false} data-id="element-922" data-mp="1xvki1dzmy6hi" />} data-id="element-921" data-mp="1xvucpevml1tj" />
                <Bar dataKey="orders" name="Órdenes" radius={[4, 4, 0, 0]} maxBarSize={40} data-id="element-923" data-mp="1svy9hhur5ef1">
                  {filteredData.map(entry => <Cell key={`cell-${entry.hour}`} fill={peakHourSet.has(entry.hour) ? '#f59e0b' : '#6366f1'} opacity={peakHourSet.has(entry.hour) ? 1 : 0.7} data-id="element-924" data-mp="22sug1i13bqgy" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>}
        {peakHours.length > 0 && <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 flex-wrap" data-id="element-925" data-mp="3qk215fc8bi7i">
            <span className="flex items-center gap-1.5" data-id="element-926" data-mp="3bvfbajoz7821">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" data-id="element-927" data-mp="3gtgwba5yphg5" />
              Horas pico
            </span>
            <span className="flex items-center gap-1.5" data-id="element-928" data-mp="8d5l434m9zta">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 opacity-70" data-id="element-929" data-mp="8bkpsbahacud" />
              Otras horas
            </span>
          </div>}
      </CardContent>
    </Card>;
}
// ─── Channel Pie Chart ─────────────────────────────────────
interface ChannelChartProps {
  data: ChannelBreakdown;
}
const CHANNEL_COLORS = ['#10b981', '#3b82f6', '#9ca3af'];
const CHANNEL_LABELS: Record<string, string> = {
  qr: 'QR (Autoservicio)',
  waiter: 'Mesero',
  other: 'Otros canales'
};
export function ChannelPieChart({
  data
}: ChannelChartProps) {
  const total = data.qr + data.waiter + data.other;
  const pieData = [{
    name: CHANNEL_LABELS.qr,
    value: data.qr,
    revenue: data.qrRevenue,
    color: CHANNEL_COLORS[0]
  }, {
    name: CHANNEL_LABELS.waiter,
    value: data.waiter,
    revenue: data.waiterRevenue,
    color: CHANNEL_COLORS[1]
  }, {
    name: CHANNEL_LABELS.other,
    value: data.other,
    revenue: data.otherRevenue,
    color: CHANNEL_COLORS[2]
  }].filter(d => d.value > 0);
  return <Card data-id="element-930" data-mp="lk6c5gjpeci8">
      <CardHeader data-id="element-931" data-mp="lky9avsy8iam">
        <CardTitle data-id="element-932" data-mp="lhaii4ew29zl">QR vs Mesero</CardTitle>
      </CardHeader>
      <CardContent data-id="element-933" data-mp="7kx9zksc1ix9">
        {total === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-934" data-mp="7lyibhhyrrba">
            No hay datos para el período seleccionado
          </div> : <div className="flex flex-col items-center" data-id="element-935" data-mp="21yensg2xr1f3">
            <div className="h-56 w-full max-w-xs" data-id="element-936" data-mp="25zqe6ntmvqoc">
              <ResponsiveContainer width="100%" height="100%" data-id="element-937" data-mp="25x4a4y5ybtav">
                <PieChart data-id="element-938" data-mp="3qiaqgcvh03zy">
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-939" data-mp="3qhiu2zu1u28m">
                    {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-940" data-mp="dyja1l3zpdos" data-mpi={index} />)}
                  </Pie>
                  <Tooltip content={({
                active,
                payload
              }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                const pct = total > 0 ? Math.round(d.value / total * 100) : 0;
                return <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm" data-id="element-942" data-mp="2jt2hp7tv2umk">
                          <p className="font-medium" data-id="element-943" data-mp="nq7i7lbcwvkv">{d.name}</p>
                          <p className="text-gray-300" data-id="element-944" data-mp="nrsat5e424g3">
                            {d.value} pedidos ({pct}%)
                          </p>
                          <p className="text-gray-300" data-id="element-945" data-mp="xnlynkxa5kd6">
                            {formatCurrency(d.revenue)}
                          </p>
                        </div>;
              }} data-id="element-941" data-mp="909idkhh4v7x" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex flex-col gap-2 mt-2 w-full" data-id="element-946" data-mp="1blsd1hmmkrmg">
              {pieData.map(entry => {
            const pct = total > 0 ? Math.round(entry.value / total * 100) : 0;
            return <div key={entry.name} className="flex items-center justify-between px-2" data-id="element-947" data-mp="1pf1kvduxd52v">
                    <div className="flex items-center gap-2" data-id="element-948" data-mp="1uew6p2zuf4h2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{
                  backgroundColor: entry.color
                }} data-id="element-949" data-mp="3ewonribzvm2x" />
                      <span className="text-sm text-gray-700" data-id="element-950" data-mp="35w0jvggbf23r">
                        {entry.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-951" data-mp="1h0r9uzff5fl">
                      <span className="text-sm font-semibold text-gray-900" data-id="element-952" data-mp="1jd6i0dsv1oy">
                        {entry.value}
                      </span>
                      <span className="text-xs text-gray-400 w-10 text-right" data-id="element-953" data-mp="3ott9jh1xhfj7">
                        {pct}%
                      </span>
                    </div>
                  </div>;
          })}
            </div>
          </div>}
      </CardContent>
    </Card>;
}
// ─── Top Products Bar Chart ────────────────────────────────
interface TopProductsChartProps {
  data: TopProduct[];
}
export function TopProductsChart({
  data
}: TopProductsChartProps) {
  const chartData = data.map(p => ({
    ...p,
    shortName: p.name.length > 20 ? p.name.slice(0, 18) + '…' : p.name
  }));
  return <Card data-id="element-954" data-mp="3n4rneehjco70">
      <CardHeader data-id="element-955" data-mp="3n5axj90y28ah">
        <CardTitle data-id="element-956" data-mp="3n5t34w9hxyv2">Productos Más Vendidos</CardTitle>
      </CardHeader>
      <CardContent data-id="element-957" data-mp="3s3m8jn2z6oua">
        {data.length === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-958" data-mp="jmzvf03eg48t">
            No hay datos para el período seleccionado
          </div> : <div className="h-72" data-id="element-959" data-mp="nqpdd6pkjnkv">
            <ResponsiveContainer width="100%" height="100%" data-id="element-960" data-mp="9xg5jah9ra4g">
              <BarChart data={chartData} layout="vertical" margin={{
            top: 0,
            right: 8,
            left: 0,
            bottom: 0
          }} data-id="element-961" data-mp="9v3t1cua5rys">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} data-id="element-962" data-mp="17dncobz7g681" />
                <XAxis type="number" tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={false} data-id="element-963" data-mp="1cpsg530rc92e" />
                <YAxis type="category" dataKey="shortName" tick={{
              fontSize: 11,
              fill: '#6b7280'
            }} tickLine={false} axisLine={false} width={120} data-id="element-964" data-mp="1linm5yy1u8t8" />
                <Tooltip content={({
              active,
              payload
            }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm" data-id="element-966" data-mp="2e7b6nsaz4u5q">
                        <p className="font-medium" data-id="element-967" data-mp="2m890lkw4cb8l">{d.name}</p>
                        <p className="text-gray-300" data-id="element-968" data-mp="2m9tsf2r1tg2r">{d.quantity} unidades</p>
                        <p className="text-gray-300" data-id="element-969" data-mp="2q79h2wr4j1sf">
                          {formatCurrency(d.revenue)}
                        </p>
                      </div>;
            }} data-id="element-965" data-mp="12rmg6qyihd06" />
                <Bar dataKey="quantity" name="Cantidad" fill="#8b5cf6" radius={[0, 4, 4, 0]} maxBarSize={28} opacity={0.85} data-id="element-970" data-mp="304w82hlp6jsr" />
              </BarChart>
            </ResponsiveContainer>
          </div>}
      </CardContent>
    </Card>;
}